import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from './AdminLayout';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { CheckCircle, XCircle, ShieldCheck } from 'lucide-react';

const ReviewActions = ({ token, application, onUpdate }) => {
  const navigate = useNavigate();
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [finalApproving, setFinalApproving] = useState(false);

  const status = application?.status;
  const hasDeployment = !!application?.deployment?.tier;
  const hasFeeSchedule = (application?.feeSchedule?.items || []).length > 0;
  const canApprove = status === 'pending' && hasDeployment && hasFeeSchedule;
  const canFinalApprove = status === 'in_review';

  const handleApprove = async () => {
    if (!hasDeployment) {
      toast.error('Deployment configuration is required');
      return;
    }
    if (!hasFeeSchedule) {
      toast.error('Fee schedule is required');
      return;
    }

    setApproving(true);
    try {
      const payload = {};
      if (reviewNotes.trim()) payload.reviewNotes = reviewNotes.trim();
      const response = await adminApi.post(`/admin/applications/${token}/approve`, payload);
      toast.success('Application approved');
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    setRejecting(true);
    try {
      const payload = { reason: rejectReason.trim() };
      if (reviewNotes.trim()) payload.reviewNotes = reviewNotes.trim();
      await adminApi.post(`/admin/applications/${token}/reject`, payload);
      toast.success('Application rejected');
      setRejectDialogOpen(false);
      navigate('/admin/applications');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setRejecting(false);
    }
  };

  const handleFinalApprove = async () => {
    setFinalApproving(true);
    try {
      const payload = {};
      if (reviewNotes.trim()) payload.reviewNotes = reviewNotes.trim();
      const response = await adminApi.post(`/admin/applications/${token}/final-approve`, payload);
      toast.success('Final approval submitted');
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit final approval');
    } finally {
      setFinalApproving(false);
    }
  };

  return (
    <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
      <h3 className="text-lg font-semibold text-slate-100 mb-4">Review Actions</h3>

      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-400">Status:</span>
          <Badge className={`text-sm ${
            status === 'pending' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
            status === 'approved' ? 'bg-green-500/20 text-green-300 border-green-500/30' :
            status === 'in_review' ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' :
            status === 'submitted' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
            status === 'rejected' ? 'bg-red-500/20 text-red-300 border-red-500/30' :
            'bg-slate-600 text-slate-300'
          } border`}>
            {(status || 'unknown').replace('_', ' ')}
          </Badge>
        </div>

        {application?.emailVerified != null && (
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck className={`h-4 w-4 ${application.emailVerified ? 'text-green-400' : 'text-slate-500'}`} />
            <span className={application.emailVerified ? 'text-green-300' : 'text-slate-400'}>
              Email {application.emailVerified ? 'verified' : 'not verified'}
            </span>
          </div>
        )}

        {status === 'pending' && (
          <div className="space-y-2 text-xs text-slate-400">
            <p className={hasDeployment ? 'text-green-400' : 'text-yellow-400'}>
              {hasDeployment ? 'Deployment configured' : 'Deployment configuration required'}
            </p>
            <p className={hasFeeSchedule ? 'text-green-400' : 'text-yellow-400'}>
              {hasFeeSchedule ? 'Fee schedule set' : 'Fee schedule required'}
            </p>
          </div>
        )}

        <div>
          <Label className="text-slate-200">Review Notes</Label>
          <textarea
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            rows={3}
            placeholder="Add review notes (optional)..."
            className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 text-slate-100 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid="review-notes"
          />
        </div>

        <div className="flex gap-3">
          {status === 'pending' && (
            <>
              <Button
                onClick={handleApprove}
                disabled={approving || !canApprove}
                className="flex-1 bg-green-600 text-white hover:bg-green-500 font-medium disabled:opacity-50"
                data-testid="approve-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {approving ? 'Approving...' : 'Approve'}
              </Button>
              <Button
                onClick={() => setRejectDialogOpen(true)}
                className="flex-1 bg-red-600 text-white hover:bg-red-500 font-medium"
                data-testid="reject-btn"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </>
          )}

          {canFinalApprove && (
            <Button
              onClick={handleFinalApprove}
              disabled={finalApproving}
              className="flex-1 bg-emerald-600 text-white hover:bg-emerald-500 font-medium"
              data-testid="final-approve-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {finalApproving ? 'Submitting...' : 'Final Approval'}
            </Button>
          )}
        </div>

        {(status === 'approved' || status === 'submitted') && (
          <p className="text-sm text-slate-400">
            This application has been {status}. No further actions available.
          </p>
        )}
        {status === 'rejected' && (
          <p className="text-sm text-red-400">
            This application was rejected.
          </p>
        )}
      </div>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Reject Application</DialogTitle>
            <DialogDescription className="text-slate-400">
              Provide a reason for rejecting this application. The merchant will be notified.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-slate-200">Rejection Reason *</Label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Reason for rejection..."
              className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 text-slate-100 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid="reject-reason"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
              className="border-slate-600 text-slate-200 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={rejecting || !rejectReason.trim()}
              className="bg-red-600 text-white hover:bg-red-500"
              data-testid="confirm-reject-btn"
            >
              {rejecting ? 'Rejecting...' : 'Confirm Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReviewActions;
